import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../api/reports';
import { useCompany } from '../contexts/CompanyContext';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';

function fmtDuration(minutes: number | null): string {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function StatCard({ label, value, tone = 'default' }: { label: string; value: string | number; tone?: 'default' | 'warning' | 'brand' }) {
  const toneClass =
    tone === 'warning'
      ? 'bg-warn/10'
      : tone === 'brand'
      ? 'bg-primary-muted'
      : 'bg-row-odd';
  return (
    <div className={`p-4 rounded-lg ${toneClass}`}>
      <p className="text-sm text-muted">{label}</p>
      <p className="text-2xl font-semibold text-fg">{value}</p>
    </div>
  );
}

function toCsv(rows: Array<Record<string, unknown>>): string {
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Reports() {
  const { currentCompany } = useCompany();
  const [groupBy, setGroupBy] = useState<'technician' | 'ticket'>('technician');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const companyKey = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const metricsQ = useQuery({
    queryKey: ['reports-metrics', from, to, companyKey],
    queryFn: () => reportsApi.metrics({ from: from || undefined, to: to || undefined }),
    enabled: !!currentCompany,
  });

  const timeQ = useQuery({
    queryKey: ['reports-time', groupBy, from, to, companyKey],
    queryFn: () => reportsApi.time({ groupBy, from: from || undefined, to: to || undefined }),
    enabled: !!currentCompany,
  });

  const ticketsQ = useQuery({
    queryKey: ['reports-tickets', from, to, companyKey],
    queryFn: () => reportsApi.tickets({ from: from || undefined, to: to || undefined }),
    enabled: !!currentCompany,
  });

  const billableQ = useQuery({
    queryKey: ['reports-billable', from, to, companyKey],
    queryFn: () => reportsApi.billable({ from: from || undefined, to: to || undefined }),
    enabled: !!currentCompany,
  });

  if (!currentCompany) {
    return (
      <div className="flex flex-col gap-4 pb-10">
        <div className="card p-6">
          <EmptyState>Select a company to view reports.</EmptyState>
        </div>
      </div>
    );
  }

  const m = metricsQ.data;
  const report = timeQ.data?.report ?? [];
  const totals = timeQ.data?.totals ?? { totalMinutes: 0, billableMinutes: 0, entries: 0 };
  const byStatus = ticketsQ.data?.byStatus ?? [];
  const billableSummary = billableQ.data?.summary ?? [];
  const totalBillableHours = billableQ.data?.totalBillableHours ?? 0;

  const anyError = metricsQ.isError || timeQ.isError || ticketsQ.isError || billableQ.isError;

  const setPreset = (days: number) => {
    const t = new Date();
    const f = new Date();
    f.setDate(f.getDate() - days);
    setTo(t.toISOString().slice(0, 10));
    setFrom(f.toISOString().slice(0, 10));
  };

  const exportTimeCsv = () => {
    const rows = report.map((r: any) => ({
      group: r.key?.name ?? r.key?.ticketNumber ?? r.key?.title ?? '',
      totalHours: Math.round((r.totalMinutes / 60) * 10) / 10,
      billableHours: Math.round((r.billableMinutes / 60) * 10) / 10,
      entries: r.entries,
    }));
    downloadCsv(`time-report-${from || 'all'}_${to || 'now'}.csv`, toCsv(rows));
  };

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="Reports"
        intro="Review ticket performance, SLA compliance, and logged time. Set a date range or quick preset, then export the time report as CSV."
      />

      <div className="card p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="label">From</label>
          <input type="date" className="input w-40" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="label">To</label>
          <input type="date" className="input w-40" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <button className="btn btn-ghost" onClick={() => setPreset(7)}>7d</button>
          <button className="btn btn-ghost" onClick={() => setPreset(30)}>30d</button>
          <button className="btn btn-ghost" onClick={() => setPreset(90)}>90d</button>
          <button className="btn btn-ghost" onClick={() => { setFrom(''); setTo(''); }}>All</button>
        </div>
        <div>
          <label className="label">Group by</label>
          <select className="select w-40" value={groupBy} onChange={(e) => setGroupBy(e.target.value as any)}>
            <option value="technician">Technician</option>
            <option value="ticket">Ticket</option>
          </select>
        </div>
      </div>

      {anyError && (
        <div className="card p-4 bg-err/10 text-err">
          Failed to load some report data. Please try again.
        </div>
      )}

      {/* Performance metrics */}
      <section>
        <h2 className="section-title mb-3">Overview</h2>
        {metricsQ.isLoading ? (
          <div className="card p-6 text-muted">Loading metrics…</div>
        ) : m ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Open" value={m.counts.open} />
            <StatCard label="Unassigned" value={m.counts.unassigned} tone={m.counts.unassigned > 0 ? 'warning' : 'default'} />
            <StatCard label="Created" value={m.counts.created} />
            <StatCard label="Resolved" value={m.counts.resolved} />
            <StatCard label="Avg first response" value={fmtDuration(m.avgFirstResponseMinutes)} />
            <StatCard label="Avg resolution" value={fmtDuration(m.avgResolutionMinutes)} />
            <StatCard label="SLA compliance" value={m.slaCompliancePct == null ? '—' : `${m.slaCompliancePct}%`} tone={m.slaCompliancePct != null && m.slaCompliancePct < 90 ? 'warning' : 'default'} />
            <StatCard label={`CSAT (${m.csatResponses})`} value={m.csatAverage == null ? '—' : `${m.csatAverage} / 5`} tone="brand" />
          </div>
        ) : null}
      </section>

      {/* Backlog age */}
      {m && (
        <div className="card p-6">
          <h2 className="section-title mb-4">Open backlog by age</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div><p className="text-2xl font-semibold text-fg">{m.backlogAge.lt1d}</p><p className="text-sm text-muted">&lt; 1 day</p></div>
            <div><p className="text-2xl font-semibold text-fg">{m.backlogAge.d1to3}</p><p className="text-sm text-muted">1–3 days</p></div>
            <div><p className="text-2xl font-semibold text-fg">{m.backlogAge.d3to7}</p><p className="text-sm text-muted">3–7 days</p></div>
            <div><p className={`text-2xl font-semibold ${m.backlogAge.gt7 > 0 ? 'text-warn' : 'text-fg'}`}>{m.backlogAge.gt7}</p><p className="text-sm text-muted">&gt; 7 days</p></div>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="section-title">Time Report</h2>
            <button className="btn btn-secondary" onClick={exportTimeCsv} disabled={report.length === 0}>Export CSV</button>
          </div>
          {timeQ.isLoading ? (
            <p className="text-muted">Loading…</p>
          ) : (
            <>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Total time</span>
                  <span className="font-medium text-fg">{Math.round((totals.totalMinutes / 60) * 10) / 10} hrs</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Billable</span>
                  <span className="font-medium text-fg">{Math.round((totals.billableMinutes / 60) * 10) / 10} hrs</span>
                </div>
              </div>
              {report.length === 0 ? (
                <EmptyState>No time logged in this range.</EmptyState>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {report.slice(0, 15).map((r: any, i: number) => (
                    <div key={i} className="flex justify-between text-sm py-2 border-b border-line-light last:border-0 text-fg">
                      <span className="truncate">{r.key?.name ?? r.key?.ticketNumber ?? r.key?.title ?? '—'}</span>
                      <span>{Math.round((r.totalMinutes / 60) * 10) / 10} hrs</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="card p-6">
          <h2 className="section-title mb-4">Tickets by status</h2>
          {ticketsQ.isLoading ? (
            <p className="text-muted">Loading…</p>
          ) : byStatus.length === 0 ? (
            <EmptyState>No tickets in this range.</EmptyState>
          ) : (
            <div className="space-y-2">
              {byStatus.map((s: any) => (
                <div key={s.status} className="flex justify-between text-sm text-fg">
                  <span className="capitalize">{String(s.status).replace(/_/g, ' ')}</span>
                  <span>{s._count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card p-6">
        <h2 className="section-title mb-4">Billable Summary</h2>
        <p className="text-2xl font-semibold text-primary mb-4">{totalBillableHours} hrs total</p>
        {billableQ.isLoading ? (
          <p className="text-muted">Loading…</p>
        ) : billableSummary.length === 0 ? (
          <EmptyState>No billable time in this range.</EmptyState>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {billableSummary.map((s: any, i: number) => (
              <div key={i} className="flex justify-between text-sm py-2 border-b border-line-light text-fg">
                <span>{s.technician?.name ?? 'Unknown'}</span>
                <span>{s.billableHours} hrs</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
